import React, { useEffect, useState } from 'react';

import useAsync from '../hooks/useAsync';
import useModal from '../hooks/useModal';

import {
	Field,
	FormRow,
	Label,
	TextField,
} from '../components/Form/tailwindForm';
import Slider from '../components/Shell/Slider';
import { addLocation, getLocations, editLocation } from '../api';
import Button from '../components/Button';
import SelectLocations from '../components/SelectLocation';

const MODAL_NAME = 'addLocation';
const defaultForm = { type: 'group' };

export default () => {
	/* Add Location Modal via `addLocation` */
	const { open, setClose } = useModal(MODAL_NAME);

	/* Using same modal for editing */
	const isEditing = !!open?.id;

	/* Setup form hook */
	const [form, setForm] = useState(defaultForm);
	const resetForm = (form = {}) => setForm({ ...defaultForm, ...form });

	/* Get all locations hook */
	const { value: locations, execute: refreshLocations } = useAsync(
		getLocations,
		{ immediate: false }
	);

	/* Add location hook */
	const {
		execute: onAdd,
		status,
		error,
		resetError,
	} = useAsync(addLocation, {
		immediate: false,
	});

	/* Edit location hook */
	const { execute: onEdit } = useAsync(editLocation, {
		immediate: false,
	});

	/* Set action if editing or not */
	const onAction = isEditing ? onEdit : onAdd;

	/* When modal opens, trigger actions */
	useEffect(() => {
		if (open) {
			/* Initial get locations */
			refreshLocations();

			/* Pass `open` into resetForm if editing */
			resetForm(open?.id ? open : {});
		} else {
			resetError();
			resetForm();
		}
	}, [open]);

	/* Standard statuses */
	const hasError = status === 'error';
	const isLoading = status === 'pending';

	/* Configure location options */
	const options = locations?.rows.map((row) => ({
		value: row.id,
		label: row.name,
		id: row.id,
	}));

	/* Standard variables */
	const title = isEditing
		? `Editing Location: ${open?.name}`
		: 'Add a location';

	return (
		<Slider name={MODAL_NAME} title={title} form={true}>
			{hasError ? (
				<div
					className="p-4 mb-4 text-xs text-red-700 bg-red-100 rounded-lg"
					role="alert"
				>
					<span className="font-medium">Uh oh, </span>something went wrong
				</div>
			) : null}

			{/*<FormRow className="mt-4">
				<Label>Location Type</Label>
				<Field>
					<Select
						options={locationTypeOptions}
						isSearchable={true}
						placeholder="Select location Type"
						onChange={(e) => setForm({ ...form, type: e.value })}
						value={locationTypeOptions.find((o) => o.value === form.type) || ''}
						style={{ width: '100%' }}
					/>
				</Field>
			</FormRow>*/}

			<FormRow className="mt-4">
				<Label>Location Name</Label>
				<Field>
					<TextField
						placeholder="Location name"
						onChange={(e) => setForm({ ...form, name: e.target.value })}
						value={form.name}
					/>
				</Field>
			</FormRow>

			<FormRow className="mt-4">
				<Label description={`(Optional)`}>Parent Location</Label>
				<Field>
					<SelectLocations
						locations={locations}
						onChange={(e) => {
							setForm({ ...form, parent_id: e });
						}}
						value={form.parent_id}
						skip={['bench']}
						preventNew
						placeholder={'Optional: Select a parent location'}
					/>
				</Field>
			</FormRow>

			<FormRow className="pt-4" alignRight>
				<Button
					onClick={() =>
						onAction(configurePayload(form), (response) => {
							if (typeof open?.onSubmit === 'function') {
								open?.onSubmit({
									label: response.name,
									value: response.id,
									id: response.id,
								});
								resetForm();
							}
							setClose();
						})
					}
				>
					{isLoading
						? isEditing
							? 'Updating...'
							: 'Adding...'
						: isEditing
						? 'Update location'
						: 'Add location'}
				</Button>
			</FormRow>
		</Slider>
	);
};

const configurePayload = ({ id, name, parent_id, type }) => ({
	id,
	name,
	parentId: parent_id || 'undefined',
	type,
});
